<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  companyData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const defaultState = {
  id: 0,
  name: '',
  domain: '',
  description: '',
  website: '',
  industry: '',
  size: '',
  revenue: '',
  phone: '',
  address: '',
};

const state = reactive({ ...defaultState });

const validationRules = {
  name: { required },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  const data = props.companyData || {};
  
  Object.assign(state, {
    id: data.id || 0,
    name: data.name || '',
    domain: data.domain || '',
    description: data.description || '',
    website: data.website || '',
    industry: data.industry || '',
    size: data.size || '',
    revenue: data.revenue || '',
    phone: data.phone || '',
    address: data.address || '',
  });
};

watch(
  () => props.companyData,
  () => {
    prepareStateBasedOnProps();
  },
  { deep: true, immediate: true }
);

watch(
  () => state,
  () => {
    emit('update', { ...state });
  },
  { deep: true }
);

defineExpose({
  state,
  isFormInvalid,
});
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        v-model="state.name"
        :label="t('COMPANIES.FORM.NAME_LABEL')"
        :placeholder="t('COMPANIES.FORM.NAME_PLACEHOLDER')"
        :error="v$.name.$error ? t('COMPANIES.FORM.NAME_REQUIRED') : ''"
      />
      <Input
        v-model="state.domain"
        :label="t('COMPANIES.FORM.DOMAIN_LABEL')"
        :placeholder="t('COMPANIES.FORM.DOMAIN_PLACEHOLDER')"
      />
    </div>
    
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANIES.FORM.DESCRIPTION_LABEL') }}
      </label>
      <Editor
        :model-value="state.description || ''"
        :placeholder="t('COMPANIES.FORM.DESCRIPTION_PLACEHOLDER')"
        class="[&>div]:min-h-[120px]"
        @update:model-value="val => state.description = val"
      />
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        v-model="state.website"
        :label="t('COMPANIES.FORM.WEBSITE_LABEL')"
        :placeholder="t('COMPANIES.FORM.WEBSITE_PLACEHOLDER')"
      />
      <Input
        v-model="state.industry"
        :label="t('COMPANIES.FORM.INDUSTRY_LABEL')"
        :placeholder="t('COMPANIES.FORM.INDUSTRY_PLACEHOLDER')"
      />
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        v-model="state.size"
        :label="t('COMPANIES.FORM.SIZE_LABEL')"
        :placeholder="t('COMPANIES.FORM.SIZE_PLACEHOLDER')"
      />
      <Input
        v-model="state.revenue"
        :label="t('COMPANIES.FORM.REVENUE_LABEL')"
        :placeholder="t('COMPANIES.FORM.REVENUE_PLACEHOLDER')"
      />
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        v-model="state.phone"
        :label="t('COMPANIES.FORM.PHONE_LABEL')"
        :placeholder="t('COMPANIES.FORM.PHONE_PLACEHOLDER')"
      />
      <Input
        v-model="state.address"
        :label="t('COMPANIES.FORM.ADDRESS_LABEL')"
        :placeholder="t('COMPANIES.FORM.ADDRESS_PLACEHOLDER')"
      />
    </div>
  </div>
</template>
